import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Clock, Edit, MailQuestion, UserPlus } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { StaffScheduleEditor } from './StaffScheduleEditor';
import type { ShopHoursMap } from '@/lib/shopHours/defaults';
import { isHoursMapEmpty } from '@/lib/shopHours/defaults';
import { FrontOfficeOptInSection, suggestFrontOfficeWindow, type FrontOfficeCandidate } from './FrontOfficeOptInSection';
import { overlappingDays, DAY_LABELS } from '@/lib/staffSchedule/frontOfficeOptIn';

interface StaffSchedule {
  user_id: string;
  role: 'MANAGER' | 'SERVICE_WRITER';
  shift_start: string;
  shift_end: string;
  working_days: number[];
  time_off?: string[];
  recurring_breaks?: Array<{
    name: string;
    start_time: string;
    end_time: string;
  }>;
  non_recurring_breaks?: Array<{
    name: string;
    date: string;
    start_time: string;
    end_time: string;
  }>;
  display_name?: string;
  /** Core-100 direction 12: a technician who also works as a Service Advisor, added explicitly. */
  multi_role?: boolean;
}

// Batch 15: a staff schedule row may come from one of three canonical
// sources so advisors / managers show up immediately after setup, well
// before any invite is accepted:
//
//   - 'active'          → row in `memberships` with status='active'
//                         (the only source that can be saved — its key
//                         is an auth user_id, matching the
//                         shop_profile.staff_schedules JSON shape).
//
//   - 'pending_invite'  → row in `team_invitations` with status='pending'
//                         and role IN ('MANAGER','SERVICE_WRITER').
//                         Read-only; the row activates once the invitee
//                         accepts the invitation.
//
//   - 'draft_from_setup'→ row in `onboarding_state.steps_completed.
//                         page3.team_members` snapshot with team_role
//                         IN ('MANAGER','SERVICE_WRITER') and no email
//                         (so no team_invitations row was created).
//                         Read-only; the operator can add an email +
//                         send an invite from Team Management to
//                         promote this entry.
//
// All three render with the same shop-hours-derived default schedule so
// the manager / writer card is never blank. Only 'active' rows persist
// edits to shop_profile.staff_schedules — the JSON shape is keyed by
// auth user_id and we don't synthesize fake user_ids for the other
// sources.
type StaffSource = 'active' | 'pending_invite' | 'draft_from_setup';

interface StaffMember {
  id: string;
  display_name: string;
  email: string | null;
  role: 'MANAGER' | 'SERVICE_WRITER';
  source: StaffSource;
}

// Lowercase day name → JS Date.getDay() index (0=Sunday..6=Saturday).
// Matches the shape `shop_profile.hours` is written with by
// commitWizard and ShopBasicsStep (Batch 12 reads stayed
// case-tolerant; writes stayed lowercase).
const LOWERCASE_DAY_TO_INDEX: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

/**
 * Derive a starter shift_start / shift_end / working_days from the
 * shop's posted hours. Used when a staff member doesn't yet have an
 * entry in shop_profile.staff_schedules (which is the case for every
 * new staff member, every pending invite, and every draft-from-setup
 * row). The earliest open time and latest close time across all open
 * days form the bracket; the open days form working_days.
 *
 * Falls back to 08:00–17:00, Mon–Fri when shop_profile.hours is empty
 * — same intent as `DEFAULT_SHOP_HOURS_MON_FRI_8_5` from
 * `lib/shopHours/defaults.ts`, kept inline here so the helper has no
 * data dependency on that import path beyond the shape.
 */
function defaultsFromShopHours(hours: ShopHoursMap | null | undefined): {
  shift_start: string;
  shift_end: string;
  working_days: number[];
} {
  if (!hours || isHoursMapEmpty(hours)) {
    return { shift_start: '08:00', shift_end: '17:00', working_days: [1, 2, 3, 4, 5] };
  }
  let earliest: string | null = null;
  let latest: string | null = null;
  const workingDaySet = new Set<number>();
  for (const [rawKey, value] of Object.entries(hours)) {
    if (!value?.open || !value?.close) continue;
    const dayIdx = LOWERCASE_DAY_TO_INDEX[String(rawKey).toLowerCase()];
    if (typeof dayIdx === 'number') workingDaySet.add(dayIdx);
    if (!earliest || value.open < earliest) earliest = value.open;
    if (!latest || value.close > latest) latest = value.close;
  }
  if (workingDaySet.size === 0 || !earliest || !latest) {
    return { shift_start: '08:00', shift_end: '17:00', working_days: [1, 2, 3, 4, 5] };
  }
  return {
    shift_start: earliest,
    shift_end: latest,
    working_days: [...workingDaySet].sort((a, b) => a - b),
  };
}

export const StaffScheduleManager = () => {
  const { activeShop, user } = useAuth();
  const [schedules, setSchedules] = useState<StaffSchedule[]>([]);
  const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);
  const [staffSourceById, setStaffSourceById] = useState<Map<string, StaffSource>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [editingSchedule, setEditingSchedule] = useState<StaffSchedule | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  // Core-100 direction 12: technicians who also work as Service Advisors (explicit opt-in only).
  const candidatesRef = useRef<Map<string, FrontOfficeCandidate>>(new Map());

  useEffect(() => {
    if (!activeShop?.id) return;
    loadData();
  }, [activeShop?.id]);

  const loadData = async () => {
    try {
      // Independent reads — fire them in parallel.
      const [profileRes, membershipsRes, invitesRes, onboardingRes] = await Promise.all([
        supabase
          .from('shop_profile')
          .select('staff_schedules, hours')
          .eq('shop_id', activeShop!.id)
          .maybeSingle(),
        supabase
          .from('memberships')
          .select('user_id, role')
          .eq('shop_id', activeShop!.id)
          .in('role', ['MANAGER', 'SERVICE_WRITER'])
          .eq('status', 'active'),
        // Pending invites — surface advisors/managers immediately after
        // setup, before they accept. Filter to non-tech roles so the
        // TECHNICIAN invites that the Batch 6 technicians-table flow
        // pre-creates don't double-render here.
        supabase
          .from('team_invitations')
          .select('id, email, role, created_at')
          .eq('shop_id', activeShop!.id)
          .eq('status', 'pending')
          .in('role', ['MANAGER', 'SERVICE_WRITER']),
        // Wizard roster snapshot — only used to surface draft rows
        // whose name was entered during setup but no email was given
        // (so no team_invitations row exists). The snapshot lives on
        // the OPERATOR's own onboarding_state row; that row is created
        // by commitWizard on setup-complete and we read it here only
        // for display.
        user
          ? supabase
              .from('onboarding_state')
              .select('steps_completed')
              .eq('user_id', user.id)
              .eq('shop_id', activeShop!.id)
              .eq('flow', 'setup_wizard')
              .maybeSingle()
          : Promise.resolve({ data: null, error: null }),
      ]);

      if (membershipsRes.error) throw membershipsRes.error;
      if (invitesRes.error) {
        console.warn('[StaffScheduleManager] team_invitations fetch failed (non-fatal):', invitesRes.error);
      }
      if (onboardingRes && 'error' in onboardingRes && onboardingRes.error) {
        console.warn('[StaffScheduleManager] onboarding_state fetch failed (non-fatal):', onboardingRes.error);
      }

      const profile = profileRes.data ?? null;
      const memberships = membershipsRes.data ?? [];
      const invites = invitesRes.data ?? [];
      const onboarding = onboardingRes?.data ?? null;

      const existingSchedules = (profile?.staff_schedules as unknown as StaffSchedule[]) || [];
      const shopHours = (profile?.hours as unknown as ShopHoursMap | null) ?? null;
      const hoursDefaults = defaultsFromShopHours(shopHours);

      // Active memberships — resolve display names via profiles.
      const activeMembers: StaffMember[] = [];
      if (memberships.length > 0) {
        const userIds = memberships.map((m: any) => m.user_id);
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('id, display_name, email')
          .in('id', userIds);

        if (profilesError) {
          console.warn('[StaffScheduleManager] profiles fetch failed (non-fatal):', profilesError);
        }

        const profileMap = new Map<string, { display_name: string | null; email: string | null }>(
          (profilesData || []).map((p: any) => [p.id, { display_name: p.display_name, email: p.email }]),
        );
        for (const m of memberships as Array<{ user_id: string; role: string }>) {
          const profile = profileMap.get(m.user_id);
          activeMembers.push({
            id: m.user_id,
            display_name: profile?.display_name || 'Unknown',
            email: profile?.email ?? null,
            role: m.role as 'MANAGER' | 'SERVICE_WRITER',
            source: 'active',
          });
        }
      }

      // Pending invites — keyed by email (active members are keyed by
      // user_id, so no collision). Dedupe across invites by email in
      // case send-team-invitation's cancel-then-insert race left an
      // extra row.
      const inviteByEmail = new Map<string, { email: string; role: 'MANAGER' | 'SERVICE_WRITER' }>();
      for (const inv of invites as Array<{ email: string; role: string }>) {
        if (!inv.email) continue;
        if (inv.role !== 'MANAGER' && inv.role !== 'SERVICE_WRITER') continue;
        inviteByEmail.set(inv.email.toLowerCase(), { email: inv.email, role: inv.role as 'MANAGER' | 'SERVICE_WRITER' });
      }

      // Build a set of emails already represented by active memberships
      // so we don't double-render an invite for someone who already
      // accepted.
      const activeEmailSet = new Set<string>();
      for (const am of activeMembers) {
        if (am.email) activeEmailSet.add(am.email.toLowerCase());
      }

      const pendingMembers: StaffMember[] = [];
      for (const [emailKey, inv] of inviteByEmail.entries()) {
        if (activeEmailSet.has(emailKey)) continue;
        pendingMembers.push({
          id: `invite:${emailKey}`,
          display_name: inv.email,
          email: inv.email,
          role: inv.role,
          source: 'pending_invite',
        });
      }

      // Draft-from-setup snapshot. Read from
      // onboarding_state.steps_completed.page3.team_members for
      // MANAGER/SERVICE_WRITER entries whose email is empty AND who
      // aren't already represented by an active membership or pending
      // invite (matched by trimmed lowercase name).
      const draftMembers: StaffMember[] = [];
      const snapshotMembers = (onboarding?.steps_completed as any)?.page3?.team_members;
      if (Array.isArray(snapshotMembers)) {
        const activeNames = new Set(activeMembers.map((m) => m.display_name.trim().toLowerCase()));
        const inviteNames = new Set(
          [...inviteByEmail.values()].map((i) => i.email.trim().toLowerCase()),
        );
        const seenDraftKeys = new Set<string>();
        for (const m of snapshotMembers as Array<{
          team_role?: string;
          name?: string;
          email?: string | null;
        }>) {
          const role = m.team_role;
          if (role !== 'MANAGER' && role !== 'SERVICE_WRITER') continue;
          const name = (m.name ?? '').trim();
          if (!name) continue;
          const email = (m.email ?? '').trim();
          // Email-bearing snapshot rows are already represented by a
          // team_invitations row; skip those here to avoid a duplicate.
          if (email) continue;
          const nameKey = name.toLowerCase();
          if (activeNames.has(nameKey)) continue;
          if (inviteNames.has(nameKey)) continue;
          if (seenDraftKeys.has(nameKey)) continue;
          seenDraftKeys.add(nameKey);
          draftMembers.push({
            id: `draft:${nameKey}`,
            display_name: name,
            email: null,
            role: role as 'MANAGER' | 'SERVICE_WRITER',
            source: 'draft_from_setup',
          });
        }
      }

      const staff: StaffMember[] = [...activeMembers, ...pendingMembers, ...draftMembers];
      const nextSourceById = new Map<string, StaffSource>();
      for (const s of staff) nextSourceById.set(s.id, s.source);

      setStaffMembers(staff);
      setStaffSourceById(nextSourceById);

      // Auto-populate schedules with shop-hours-derived defaults for
      // anyone without an entry yet. Existing schedules keyed by user_id
      // continue to be honored. Read-only sources still get a row in
      // the rendered list so the UI is never blank.
      const autoSchedules: StaffSchedule[] = [...existingSchedules];
      staff.forEach((member) => {
        const existingIndex = autoSchedules.findIndex((s) => s.user_id === member.id);
        if (existingIndex === -1) {
          autoSchedules.push({
            user_id: member.id,
            role: member.role,
            shift_start: hoursDefaults.shift_start,
            shift_end: hoursDefaults.shift_end,
            working_days: hoursDefaults.working_days,
            time_off: [],
            recurring_breaks: [],
            non_recurring_breaks: [],
            display_name: member.display_name,
          });
        } else {
          autoSchedules[existingIndex].display_name = member.display_name;
        }
      });

      setSchedules(autoSchedules);
    } catch (error: any) {
      console.error('Error loading staff schedules:', error);
      toast.error('Failed to load staff schedules');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (schedule: StaffSchedule) => {
    setEditingSchedule(schedule);
    setIsEditorOpen(true);
  };

  const handleAddFrontOffice = (c: FrontOfficeCandidate) => {
    const window = suggestFrontOfficeWindow(c.tech);
    setEditingSchedule({
      user_id: c.user_id,
      role: 'SERVICE_WRITER',
      ...window,
      working_days: [1, 2, 3, 4, 5],
      time_off: [],
      recurring_breaks: [],
      non_recurring_breaks: [],
      display_name: c.display_name,
      multi_role: true,
    });
    setIsEditorOpen(true);
  };

  const persist = async (next: StaffSchedule[]) => {
    const schedulesToSave = next
      .filter((s) => {
        const src = staffSourceById.get(s.user_id);
        return !src || src === 'active';
      })
      .map(({ display_name, ...rest }) => rest);
    return supabase
      .from('shop_profile')
      .update({ staff_schedules: schedulesToSave } as never)
      .eq('shop_id', activeShop!.id);
  };

  const handleRemoveFrontOffice = async (userId: string) => {
    const next = schedules.filter((s) => s.user_id !== userId);
    const { error } = await persist(next);
    if (error) { toast.error('Failed to update the front-office schedule'); return; }
    setSchedules(next);
    toast.success('Removed from the front-office schedule');
  };

  const handleSave = async (updatedSchedule: StaffSchedule) => {
    // Core-100 direction 12: an opted-in technician's front-office hours may not overlap their bay shift.
    if (updatedSchedule.multi_role) {
      const clash = overlappingDays(updatedSchedule, candidatesRef.current.get(updatedSchedule.user_id)?.tech ?? null);
      if (clash.length > 0) {
        toast.error('These hours overlap their bay shift', {
          description: `${clash.map((d) => DAY_LABELS[d]).join(', ')}: an hour counts as front-office or technician time, not both.`,
        });
        return;
      }
    }
    try {
      // Only active memberships can persist edits — the
      // shop_profile.staff_schedules JSON shape is keyed by auth user_id
      // and the read-only sources (pending_invite / draft_from_setup)
      // use synthetic ids that aren't valid user_ids. The edit button
      // is hidden for those sources, but enforce here as a guardrail.
      const source = staffSourceById.get(updatedSchedule.user_id);
      if (source && source !== 'active') {
        toast.error('Activate this team member before saving a schedule');
        return;
      }

      const updatedSchedules = schedules.some((s) => s.user_id === updatedSchedule.user_id)
        ? schedules.map((s) => (s.user_id === updatedSchedule.user_id ? updatedSchedule : s))
        : [...schedules, updatedSchedule];

      // Persist only schedules for active memberships — keyed by an
      // auth user_id. Strip transient display_name and any
      // read-only-sourced rows so we don't write synthetic ids into
      // shop_profile.staff_schedules.
      const schedulesToSave = updatedSchedules
        .filter((s) => {
          const src = staffSourceById.get(s.user_id);
          return !src || src === 'active';
        })
        .map(({ display_name, ...rest }) => rest);

      const { error } = await supabase
        .from('shop_profile')
        .update({ staff_schedules: schedulesToSave })
        .eq('shop_id', activeShop!.id);

      if (error) throw error;

      setSchedules(updatedSchedules);
      toast.success('Staff schedule saved successfully');
    } catch (error: any) {
      console.error('Error saving schedule:', error);
      const message = String(error?.message ?? '');
      toast.error('Failed to save schedule', message.startsWith('FOH_SCHEDULE_')
        ? { description: message.replace(/^FOH_SCHEDULE_[A-Z_]+:\s*/, '') }
        : undefined);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">Loading staff...</div>
        </CardContent>
      </Card>
    );
  }

  if (staffMembers.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Staff Schedules
          </CardTitle>
          <CardDescription>
            Configure work schedules for managers and service writers (for time clock reporting only)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            No managers or service writers found. Add team members in Team Management first.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card data-testid="staff-schedule-manager">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Staff Schedules
          </CardTitle>
          <CardDescription>
            Configure work schedules for managers and service writers (for time clock reporting only).
            Pending invites and setup drafts appear here read-only until the invitee accepts.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {schedules.map((schedule) => {
              const source = staffSourceById.get(schedule.user_id) ?? 'active';
              const isReadOnly = source !== 'active';
              return (
                <div
                  key={schedule.user_id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                  data-testid={`staff-schedule-${schedule.user_id}`}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium" data-testid="staff-member-name">{schedule.display_name}</h4>
                      {source === 'pending_invite' && (
                        <Badge variant="outline" className="gap-1">
                          <MailQuestion className="h-3 w-3" />
                          Pending invite
                        </Badge>
                      )}
                      {schedule.multi_role && (
                        <Badge variant="secondary" data-testid={`front-office-opt-in-badge-${schedule.user_id}`}>
                          Technician · also front office
                        </Badge>
                      )}
                      {source === 'draft_from_setup' && (
                        <Badge variant="outline" className="gap-1">
                          <UserPlus className="h-3 w-3" />
                          Draft from setup
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground" data-testid="staff-member-role">
                      {schedule.role.replace('_', ' ')} • {schedule.shift_start} - {schedule.shift_end}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(schedule)}
                    disabled={isReadOnly}
                    title={
                      isReadOnly
                        ? source === 'pending_invite'
                          ? 'Activate by accepting the invitation'
                          : 'Add an email and send an invite from Team Management'
                        : undefined
                    }
                    data-testid={`edit-staff-schedule-${schedule.user_id}`}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit Schedule
                  </Button>
                  {schedule.multi_role && (
                    <Button variant="ghost" size="sm" onClick={() => void handleRemoveFrontOffice(schedule.user_id)}
                      data-testid={`remove-front-office-${schedule.user_id}`}>
                      Remove
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
          {activeShop?.id && (
            <FrontOfficeOptInSection
              shopId={activeShop.id}
              scheduledUserIds={new Set(schedules.map((s) => s.user_id))}
              onCandidates={(m) => { candidatesRef.current = m; }}
              onAdd={handleAddFrontOffice}
            />
          )}
        </CardContent>
      </Card>

      <StaffScheduleEditor
        schedule={editingSchedule}
        open={isEditorOpen}
        onOpenChange={setIsEditorOpen}
        onSave={handleSave}
      />
    </>
  );
};
